import { defineStore } from 'pinia';

// This store keeps the expanded state of HubBox.vue components (prop: boxId).
// So UI will remember, which card is opened or closed. Although after reload.

interface HubBoxExpandState {
  /** boxIds of HubBox components that are currently expanded */
  expandedBoxes: string[];
  /** boxIds of HubBox components that were explicitly closed */
  closedBoxes: string[];
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/hubBoxExpand.ts` (namespace
 * `hubBoxExpand`). Persisted to `localStorage` (full state) — matches the legacy `vuexLocal`
 * reducer's `hubBoxExpand: state.hubBoxExpand` entry in `src/store/index.ts`.
 *
 * No collisions/renames: getters `boxIsExpanded`/`boxIsClosed` and mutations
 * `addBoxId`/`removeBoxId`/`closeBoxId` all convert 1:1 with the same names (mutations become
 * same-named actions).
 */
export const useHubBoxExpandStore = defineStore('hubBoxExpand', {
  state: (): HubBoxExpandState => ({
    expandedBoxes: [],
    closedBoxes: [],
  }),
  getters: {
    boxIsExpanded(state): (value: string) => boolean {
      return (value: string) => !!state.expandedBoxes.includes(value);
    },
    boxIsClosed(state): (value: string) => boolean {
      return (value: string) => !!state.closedBoxes.includes(value);
    },
  },
  actions: {
    /**
     * Converted from legacy mutation `addBoxId`.
     */
    addBoxId(value: string) {
      if (this.expandedBoxes.includes(value)) return;
      this.expandedBoxes.push(value);
    },
    /**
     * Converted from legacy mutation `removeBoxId`.
     */
    removeBoxId(value: string) {
      const index = this.expandedBoxes.findIndex((c) => c === value);
      this.expandedBoxes.splice(index, 1);
    },
    /**
     * Converted from legacy mutation `closeBoxId`.
     */
    closeBoxId(value: string) {
      if (this.closedBoxes.includes(value)) return;
      this.closedBoxes.push(value);
    },
  },
  vuexPersist: {
    local: true,
  },
});
